/**
 * Types in the Wiki (`project/:p/wiki/types`): the project's page types as a map,
 * or as a table with `?view=table`, each opening its type page. "New type..." opens the New type dialog
 * (the lazy Set type chunk) with the tracker room's define-type write.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadSetPageType, useCollabDocsUI, useCollabPagesState, type CollabTypeTreeResolver } from '@nimbalyst/collab-bundle/docs-ui';
import { useDocsShell } from '../collab/CollabDocsShell';
import { CollabLoadingPanel } from '../components/Chrome';
import { orgTypePagePath } from '../routing';
import { usePagesTrackers } from './PagesTrackers';
import { usePageModule } from './pageModule';

type SetPageTypeModule = Awaited<ReturnType<typeof loadSetPageType>>;
type DefineType = NonNullable<ReturnType<typeof usePagesTrackers>['defineType']>;

function NewTypeDialogMount({ resolver, defineType, onClose, onCreated }: {
  resolver: CollabTypeTreeResolver;
  defineType: DefineType;
  onClose: () => void;
  onCreated: (typeId: string) => void;
}) {
  const { session } = useCollabDocsUI();
  const [types, setTypes] = useState<SetPageTypeModule | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    loadSetPageType().then((loaded) => { if (live) setTypes(loaded); }, (error: unknown) => {
      console.error('[WebPagesTypesRoute] New type could not load:', error);
      if (live) setFailed(true);
    });
    return () => { live = false; };
  }, []);
  useEffect(() => {
    if (failed) onClose();
  }, [failed, onClose]);
  if (!types) return null;
  return <types.NewTypeDialog lane="team" resolver={resolver} session={session} defineType={defineType} onCreated={onCreated} onClose={onClose} />;
}

export function WebPagesTypesRoute() {
  const { team, project, projectId, navigate } = useDocsShell();
  const { available, resolver, defineType } = usePagesTrackers();
  const { typePlacements } = useCollabPagesState();
  const module = usePageModule();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const view = new URLSearchParams(location.search).get('view') === 'table' ? 'table' : 'map';
  if (!available || !module) return <CollabLoadingPanel title="Opening Types" detail="Connecting to your team's pages." />;
  const { PagesTypesView } = module;
  return (
    <PagesTypesView
      lane="team"
      title={project.name}
      typePlacements={typePlacements}
      view={view}
      onViewChange={(next) => routerNavigate({ search: next === 'table' ? '?view=table' : '' }, { replace: true })}
      onOpenType={(typeId) => navigate(orgTypePagePath(team, projectId, typeId))}
      renderNewType={resolver && defineType ? ({ onClose, onCreated }) => (
        <NewTypeDialogMount resolver={resolver} defineType={defineType} onClose={onClose} onCreated={onCreated} />
      ) : undefined}
    />
  );
}
